import { useLayoutEffect, useMemo, useRef } from "react";
import { Alert, Box, Grid2 as Grid } from "@mui/material";
import { TabContent } from "@/components/common/PageLayout";
import { usePolicyContext } from "@/resources/claimants/components/PolicyContext";
import { PolicyStatus } from "@/types";
import { AggregateDeductibleSummary } from "./AggregateDeductibleSummary";
import { AggregateAccommodationSummary } from "./AggregateAccommodationSummary";
import { BreachSection } from "./BreachSection";
import { AccommodationTable } from "./AccommodationTable";

export const AggregateDeductibleTab = () => {
  const { policies, selectedPolicyId, selectedPolicy, setSelectedPolicyId } = usePolicyContext();

  // The page policy selector defaults to "All Policies", but aggregate views are
  // per-policy. Default to the active aggregate-deductible policy so the tab is
  // populated on open instead of prompting (#5580) — mirrors the Specific Claims
  // tab being usable by default. Most recent active policy with aggregate
  // deductible enabled, by effective_start_date.
  const defaultPolicy = useMemo(
    () =>
      policies
        .filter((p) => p.status === PolicyStatus.ACTIVE && p.aggregate_benefit?.is_aggregate_deductible)
        .sort((a, b) => (b.effective_start_date ?? "").localeCompare(a.effective_start_date ?? ""))[0],
    [policies]
  );

  // One-shot per mount: seed the shared selector when nothing is selected so the
  // selector label and tab content stay consistent. The ref guard keeps an
  // explicit user choice (including "All Policies") from being overridden on
  // re-render. useLayoutEffect seeds before paint so the prompt doesn't flash.
  const seededRef = useRef(false);
  useLayoutEffect(() => {
    if (!selectedPolicyId && !seededRef.current && defaultPolicy) {
      seededRef.current = true;
      setSelectedPolicyId(defaultPolicy.id);
    }
  }, [selectedPolicyId, defaultPolicy, setSelectedPolicyId]);

  if (!selectedPolicy) {
    return (
      <TabContent>
        <Alert severity="info">Select a specific policy to view aggregate deductible data.</Alert>
      </TabContent>
    );
  }

  if (!selectedPolicy.aggregate_benefit?.is_aggregate_deductible) {
    return (
      <TabContent>
        <Alert severity="info">This policy does not have aggregate deductible enabled.</Alert>
      </TabContent>
    );
  }

  return (
    <TabContent>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <AggregateDeductibleSummary
              policyId={selectedPolicy.id}
              aggregateDeductible={selectedPolicy.aggregate_benefit?.aggregate_deductible}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AggregateAccommodationSummary policyId={selectedPolicy.id} />
          </Grid>
        </Grid>
      </Box>

      <BreachSection policyId={selectedPolicy.id} />

      <AccommodationTable />
    </TabContent>
  );
};
